// Identity flow: phone OTP via Supabase Auth, then for this specific
// install register a *device* row that carries this install's
// libsignal bundle + outer-wrapper keypair. A user with N installs has
// N device rows; senders fan out per device.

import type { Identity } from '../types';
import { generateIdentityKeyPair } from './crypto';
import { getDb } from './db';
import { fetchUserDevices, publishDevice } from './relay';
import {
  getSignalStore,
  hasSignalIdentity,
  setupSignalIdentity,
} from './signal';
import { loadIdentity, saveIdentity } from './store';

const PHONE_E164 = /^\+[1-9]\d{6,14}$/;
const USERNAME = /^[a-z0-9_.-]{2,32}$/;

export function normalizePhone(input: string): string {
  const trimmed = input.trim();
  const sign = trimmed.startsWith('+') ? '+' : '';
  return sign + trimmed.replace(/[^\d]/g, '');
}

export function isValidPhone(phone: string): boolean {
  return PHONE_E164.test(phone);
}

export function isValidUsername(username: string): boolean {
  return USERNAME.test(username);
}

export async function sendOtp(phone: string): Promise<void> {
  if (!isValidPhone(phone)) {
    throw new Error('Phone must be in E.164 format, e.g. +491701234567');
  }
  const { error } = await getDb().auth.signInWithOtp({
    phone,
    options: { shouldCreateUser: true },
  });
  if (error) throw error;
}

export async function verifyOtp(
  phone: string,
  token: string,
): Promise<{ userId: string }> {
  const { data, error } = await getDb().auth.verifyOtp({
    phone,
    token: token.trim(),
    type: 'sms',
  });
  if (error) throw error;
  if (!data.user) throw new Error('verifyOtp returned no user');
  return { userId: data.user.id };
}

/**
 * Look up the identity row for the currently authenticated user, if any.
 * Used on app start to know whether this auth user has an identity yet
 * (and if so, what username).
 */
export async function fetchOwnIdentityRow(userId: string): Promise<{
  id: string;
  username: string;
} | null> {
  const { data, error } = await getDb()
    .from('identities')
    .select('id, username')
    .eq('id', userId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

/**
 * The user has verified their phone (so we have a userId) and picked a
 * username. This:
 *   1. Creates the identities row (or updates the username on an
 *      existing row if they're re-publishing).
 *   2. Generates a fresh TweetNaCl outer keypair + libsignal bundle
 *      for THIS install.
 *   3. Publishes the device row to the relay.
 *   4. Persists the local Identity (outer seckey + device_id) to
 *      localStorage.
 */
export async function publishIdentityForUser(args: {
  userId: string;
  username: string;
}): Promise<Identity> {
  let clean = args.username.trim().toLowerCase();
  if (!isValidUsername(clean)) {
    throw new Error(
      'Username: 2–32 chars, lowercase letters, digits, _ . - only.',
    );
  }

  // 1. Find or create the identities row.
  //
  // If a row already exists for this auth user (which it will whenever
  // a returning user OTPs on a new device), we use the EXISTING username
  // and silently ignore whatever was passed in. The phone number — via
  // auth.uid — is the owner of the username. Renames happen explicitly
  // through Settings → Change username, never as a side effect of
  // registering a new device.
  const { data: existing, error: lookupErr } = await getDb()
    .from('identities')
    .select('id, username')
    .eq('id', args.userId)
    .maybeSingle();
  if (lookupErr) throw lookupErr;

  if (existing) {
    clean = existing.username; // canonical name is the one already on the relay
  } else {
    const { error } = await getDb()
      .from('identities')
      .insert({ id: args.userId, username: clean });
    if (error) {
      if (error.code === '23505') {
        throw new Error(`Username "${clean}" is already taken. Pick another.`);
      }
      throw error;
    }
  }

  // 2. Generate this device's outer keypair + signal bundle.
  const outerKp = generateIdentityKeyPair();
  const store = getSignalStore(args.userId);
  const setup = await setupSignalIdentity({ userId: args.userId, store });

  // 3. Publish the device row.
  const deviceId = await publishDevice({
    user_id: args.userId,
    label: detectDeviceLabel(),
    outer_pubkey_b64: outerKp.publicKey_b64,
    bundle: setup.bundle,
    one_time_prekeys: setup.one_time_prekeys,
  });

  // 4. Persist locally.
  const identity: Identity = {
    id: args.userId,
    username: clean,
    device_id: deviceId,
    device_label: detectDeviceLabel(),
    outer_pubkey_b64: outerKp.publicKey_b64,
    outer_seckey_b64: outerKp.privateKey_b64,
  };
  saveIdentity(identity);
  return identity;
}

/**
 * Idempotent: make sure this install has a device row on the relay.
 *
 * Three cases:
 *   - Fresh install (no local identity → not called here; Register flow
 *     handles).
 *   - Local identity present and device_id is registered on relay → no-op.
 *   - Local identity present but device_id missing from relay (e.g. data
 *     was reset server-side, or the local device_id is from the pre-
 *     linked-devices schema and doesn't exist) → re-publish a device row
 *     with fresh keys and update local state.
 */
export async function ensureDeviceRegistered(
  identity: Identity,
): Promise<Identity> {
  const devices = await fetchUserDevices(identity.id);
  if (devices.some((d) => d.id === identity.device_id)) return identity;

  // We have a local Identity but the relay doesn't know this device.
  // Re-publish under a fresh device row.
  const store = getSignalStore(identity.id);
  const already = await hasSignalIdentity(store);
  const setup = already
    ? null
    : await setupSignalIdentity({ userId: identity.id, store });

  const setupForBundle =
    setup ??
    (await setupSignalIdentity({ userId: identity.id, store }));
  // (If we already had a Signal identity but the relay forgot the device,
  // the cleanest fix is to mint a fresh one. The pre-existing local
  // session keys are stale anyway because the prekey ids don't match
  // anything the relay has.)

  const newDeviceId = await publishDevice({
    user_id: identity.id,
    label: identity.device_label || detectDeviceLabel(),
    outer_pubkey_b64: identity.outer_pubkey_b64,
    bundle: setupForBundle.bundle,
    one_time_prekeys: setupForBundle.one_time_prekeys,
  });

  const updated: Identity = { ...identity, device_id: newDeviceId };
  saveIdentity(updated);
  return updated;
}

/**
 * Resume an existing identity for the currently authenticated user. Used
 * when the app boots and a Supabase Auth session is already restored.
 */
export async function resumeIdentity(userId: string): Promise<Identity | null> {
  const local = loadIdentity();
  if (local && local.id === userId) return local;
  return null;
}

export async function signOut(): Promise<void> {
  await getDb().auth.signOut();
}

/**
 * Best-effort guess at a human label for the current install. Used on
 * the linked-devices list so the user can tell their phone apart from
 * their laptop.
 */
export function detectDeviceLabel(): string {
  if (typeof navigator === 'undefined') return 'unknown device';
  const ua = navigator.userAgent;
  // Order matters: tablets often include "Mobile" in their UA too.
  if (/iPad/i.test(ua)) return 'iPad';
  if (/iPhone/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua)) {
    if (/Mobile/i.test(ua)) return 'Android phone';
    return 'Android tablet';
  }
  if (/Macintosh/i.test(ua)) return 'Mac';
  if (/Windows/i.test(ua)) return 'Windows PC';
  if (/Linux/i.test(ua)) return 'Linux PC';
  return 'browser';
}
